import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import { AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { getColumnId, getColumnsWithPinnedProp, onTableSettingsConfirm } from 'components/TableSettings';
import { TitleWithTooltipColumns } from 'components/TitleWithTooltipColumns';
import { WorkInSumTitles, WorkOrderChapterMap } from 'constantsProject';
import type {
    ChapterEnum,
    ContractStatusEnum,
    FilterValueOfWorkOrderPriceCodeFilterData,
    WorkInSumEnum,
    WorkOrderActKc2Data,
    WorkOrderPriceItem,
    WorkOrderPriceStatusEnum,
    WorkOrderSeasonData,
} from 'gql/graphql';
import { WorkOrderPriceTypeEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { usePin } from 'hooks/usePin';
import { useSelection } from 'hooks/useSelection';
import { badgePropsByStatus } from 'pages/Project/Common/CodeCell';
import { getNumberFormatter } from 'pages/Project/Common/utils';
// TODO: перенести все из workOrderPrice после полного обновления
import {
    groupNameHeaderName,
    useTypeWorkColumns,
} from 'pages/Project/UerProject/WorkOrder/WorkOrderPrice/hooks/useTypeWorkColumns';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import { getWorkOrderUnitCellValue } from '../../../helpersUerProject';
import { WorkOrderPriceCodeCell } from '../../WorkOrderPrice/WorkOrderPriceCodeCell';
import { StatusCellRenderer, WorkOrderMenuCellRenderer } from '../cellRenderers';
import type { WorkOrderMenuActionTypes } from '../types';
import { COLUMN_NAMES_WITH_FILTER } from '../types';
import {
    uerWorkOrderPriceItemChapterFilterGql,
    uerWorkOrderPriceItemCodeFilterGql,
    uerWorkOrderPriceItemNameFilterGql,
    uerWorkOrderPriceItemStatusFilterGql,
    uerWorkOrderPriceItemUnitFilterGql,
    uerWorkOrderPriceItemWorkInSumFilterGql,
    uerWorkOrderPriceItemWorkNameFilterGql,
} from '../workOrderColumnsFilterQueries';

import { useWorkOrderActsColumns } from './useWorkOrderActsColumns';
import { useWorkOrderSeasonColumns } from './useWorkOrderSeasonColumns';
import { useWorkOrderWorkRatioInfo } from './useWorkOrderWorkRatioInfo';

type TColumnsProps = {
    workOrderId: number;
    objectId: number | undefined;
    subObjectId: number | undefined;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    workOrderHasInProgress: boolean;
    costRounding: number;
    refreshPriceListData: () => void;
    seasons: WorkOrderSeasonData[];
    contractStatus: ContractStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    workOrderActs: WorkOrderActKc2Data[] | undefined | null;
    refreshActs: () => void;
    constructionTypeId: number;
    changeFilterHandler: ({ fieldName, checkedValues }: any) => void;
    skzsPanelRefresh: () => void;
    openModalHandler: (type: WorkOrderMenuActionTypes, rowData: WorkOrderPriceItem, options?: any) => void;
    hasWorkKind: boolean | null | undefined;
    refreshProjectData?: () => void;
    useAgCellRowDataRefresh: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams: any;
};

const getHeaderTitleTooltipCost = () =>
    TitleWithTooltipColumns({
        title: 'Стоимость всего, руб. без НДС',
        childTooltip: 'Стоимость всего в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС',
    });

const getHeaderTitleTooltipUnitCost = () =>
    TitleWithTooltipColumns({
        title: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
        childTooltip: 'Стоимость единицы в текущем уровне цен (с учетом прочих и лимитированных затрат), руб. без НДС',
    });

export const getCodeCellClass = ({ data }: { data: WorkOrderPriceItem | undefined }) => {
    if (data?.type === WorkOrderPriceTypeEnum.Uer || data?.type === WorkOrderPriceTypeEnum.Other) {
        return 'textLeft';
    }

    return 'centeredAgGridCell';
};

export const useWorkOrderColumns = (props: TColumnsProps) => {
    const {
        workOrderId,
        workOrderStatus,
        workOrderHasInProgress,
        costRounding,
        refreshPriceListData,
        seasons,
        workOrderActs,
        contractStatus,
        isDisabledByProjectStatus,
        constructionTypeId,
        changeFilterHandler,
        objectId,
        subObjectId,
        refreshActs,
        skzsPanelRefresh,
        openModalHandler,
        hasWorkKind,
        refreshProjectData,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
    } = props;

    const [selection, , , setSelection] = useSelection(`workOrderTable_${workOrderId}`);
    const [pinnedItems, , , setPinned] = usePin(`workOrderTable_${workOrderId}`);
    const [checkedRows, setCheckedRows] = useState({});

    const checkRowHandler = useCallback(({ id, data }) => {
        setCheckedRows((prev) => ({ ...prev, [id]: data }));
    }, []);

    const resetCheckedRows = useCallback(() => {
        setCheckedRows({});
    }, []);

    const actColumns = useWorkOrderActsColumns({
        workOrderActs,
        seasons,
        workOrderStatus,
        costRounding,
        contractStatus,
        onActEditRefresh: () => {
            refreshActs?.();
            refreshPriceListData?.();
            refreshProjectData?.();
        },
        refreshActs: () => {
            refreshActs?.();
        },
        isDisabledByProjectStatus,
        skzsPanelRefresh,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
    });

    const seasonsAndLinkedActsColumns = useWorkOrderSeasonColumns({
        workOrderStatus,
        actsColumns: actColumns,
        contractStatus,
        isDisabledByProjectStatus,
        hasInProgress: workOrderHasInProgress,
        seasons,
        costRounding,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
    });

    const { hasEmptyWorkRatios, refreshWorkRatioInfo } = useWorkOrderWorkRatioInfo({
        constructionTypeId,
        workOrderId,
    });

    const typeWorkColumns = useTypeWorkColumns({
        workOrderStatus,
        workOrderHasInProgress,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
        hasEmptyWorkRatios,
        onWorkRatioEditSuccess: refreshWorkRatioInfo,
    });

    const filterVariables = useMemo(
        () => ({
            workOrderId,
            constructionTypeId,
            rootObjectId: objectId,
            objectId: subObjectId,
        }),
        [constructionTypeId, objectId, subObjectId, workOrderId],
    );

    const columns = useMemo<(ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[]>(
        () => [
            {
                headerName: '',
                field: 'menu' as any,
                maxWidth: 80,
                minWidth: 80,
                pinned: 'left',
                lockPosition: 'left',
                cellClass: 'centeredAgGridCell',
                cellRenderer: WorkOrderMenuCellRenderer,
                cellRendererParams: {
                    workOrderStatus,
                    openModalHandler,
                    checkedRows,
                    checkRowHandler,
                    isDisabledByProjectStatus,
                },
            },
            {
                headerName: 'Объект',
                field: 'rootObjectName',
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Подобъект',
                field: 'objectName',
                flex: 1,
                minWidth: 150,
            },
            {
                headerName: 'Раздел',
                field: COLUMN_NAMES_WITH_FILTER.chapter,
                flex: 1,
                minWidth: 150,
                valueFormatter: ({ value }: { value: ChapterEnum | undefined }) =>
                    value ? WorkOrderChapterMap[value] : '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceItemChapterFilter',
                    query: uerWorkOrderPriceItemChapterFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            valueFormatter: ({ value }: { value: ChapterEnum | undefined }) =>
                                value ? WorkOrderChapterMap[value] : '',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Статус',
                field: COLUMN_NAMES_WITH_FILTER.status,
                width: 110,
                cellRenderer: StatusCellRenderer,
                tooltipComponent: null,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceItemStatusFilter',
                    query: uerWorkOrderPriceItemStatusFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Статус',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                            cellRenderer: ({ value }: { value: WorkOrderPriceStatusEnum }) =>
                                value ? badgePropsByStatus[value].label : '',
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Шифр',
                field: COLUMN_NAMES_WITH_FILTER.code,
                width: 160,
                cellClass: getCodeCellClass,
                cellRenderer: ({ data }: { data: WorkOrderPriceItem }) =>
                    !data ? '' : <WorkOrderPriceCodeCell row={data} workOrderStatus={workOrderStatus} />,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceItemCodeFilter',
                    query: uerWorkOrderPriceItemCodeFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        { headerName: '', lockVisible: true, checkboxSelection: true, width: 50 },
                        {
                            headerName: 'Расценка',
                            flex: 1,
                            cellRenderer: ({ data }: { data: FilterValueOfWorkOrderPriceCodeFilterData }) =>
                                data?.value?.isUer ? data?.value?.uerCode : '',
                        },
                        {
                            headerName: 'МТР/Коэф.',
                            flex: 1,
                            cellRenderer: ({ data }: { data: FilterValueOfWorkOrderPriceCodeFilterData }) =>
                                !data?.value?.isUer ? data?.value?.code : '',
                        },
                    ],
                    changeFilterHandler,
                    customWhereVariablesGetter: (searchString: string) => ({
                        or: [
                            { value: { code: { containsi: searchString } } },
                            { value: { uerCode: { containsi: searchString } } },
                        ],
                    }),
                },
            },
            {
                headerName: 'Наименование',
                field: COLUMN_NAMES_WITH_FILTER.name,
                width: 400,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceItemNameFilter',
                    query: uerWorkOrderPriceItemNameFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Ед. изм.',
                field: COLUMN_NAMES_WITH_FILTER.unit,
                width: 114,
                cellClass: 'centeredAgGridCell',
                valueGetter: getWorkOrderUnitCellValue,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    withSearch: false,
                    hideTableHeader: true,
                    variables: filterVariables,
                    dataAccessor: 'uerWorkOrderPriceItemUnitFilter',
                    query: uerWorkOrderPriceItemUnitFilterGql,
                    childFieldAccessor: 'value',
                    whereVariables: ['value'],
                    columns: [
                        {
                            headerName: 'Ед. изм.',
                            field: 'value',
                            checkboxSelection: true,
                            lockVisible: true,
                            flex: 1,
                        },
                    ],
                    changeFilterHandler,
                },
            },
            {
                headerName: 'Стоимость единицы в текущем уровне цен, руб. без НДС',
                headerComponent: getHeaderTitleTooltipUnitCost,
                field: 'unitCost',
                width: 180,
                wrapHeaderText: true,
                cellClass: 'ag-right-aligned-cell',
                valueFormatter: getNumberFormatter(2),
            },
            {
                headerName: 'План по НЗ',
                headerClass: 'flexStart',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Объем всего',
                        field: 'totalPlanVolume',
                        width: 125,
                        cellClass: 'ag-right-aligned-cell boldCell',
                        valueFormatter: getNumberFormatter(3, 6),
                    },
                    {
                        headerComponent: getHeaderTitleTooltipCost,
                        valueFormatter: getNumberFormatter(costRounding),
                        field: 'totalPlanCost',
                        width: 160,
                        cellClass: 'ag-right-aligned-cell boldCell',
                    },
                ],
            },
            ...(workOrderStatus === WorkOrderStatusEnum.InProgress || workOrderStatus === WorkOrderStatusEnum.Completed
                ? [
                      {
                          headerName: 'Выполнено по НЗ',
                          headerClass: 'flexStart',
                          marryChildren: true,
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalCompletedVolume',
                                  width: 125,
                                  cellClass: 'ag-right-aligned-cell',
                                  headerClass: 'ag-right-aligned-header',
                                  valueFormatter: getNumberFormatter(3, 6),
                              },
                              {
                                  headerComponent: getHeaderTitleTooltipCost,
                                  valueFormatter: getNumberFormatter(costRounding),
                                  field: 'totalCompletedCost',
                                  width: 160,
                                  cellClass: 'ag-right-aligned-cell',
                              },
                          ],
                      },
                      {
                          headerName: 'Остаток по НЗ',
                          marryChildren: true,
                          headerClass: 'flexStart',
                          children: [
                              {
                                  headerName: 'Объем всего',
                                  field: 'totalRemainderVolume',
                                  width: 125,
                                  cellClass: 'ag-right-aligned-cell',
                                  headerClass: 'ag-right-aligned-header',
                                  valueFormatter: getNumberFormatter(3, 6),
                              },
                              {
                                  headerComponent: getHeaderTitleTooltipCost,
                                  valueFormatter: getNumberFormatter(costRounding),
                                  field: 'totalRemainderCost',
                                  width: 160,
                                  cellClass: 'ag-right-aligned-cell',
                              },
                          ],
                      },
                  ]
                : []),
            ...(typeWorkColumns && hasWorkKind
                ? (typeWorkColumns as any[]).map((typeCol: ColGroupDef) => ({
                      ...typeCol,
                      children: typeCol.children?.map((child: ColDef) => {
                          if (child?.field === 'workName') {
                              return {
                                  ...child,
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      variables: filterVariables,
                                      dataAccessor: 'uerWorkOrderPriceItemWorkNameFilter',
                                      query: uerWorkOrderPriceItemWorkNameFilterGql,
                                      childFieldAccessor: 'value',
                                      whereVariables: ['value'],
                                      columns: [
                                          {
                                              headerName: 'Наименование',
                                              field: 'value',
                                              checkboxSelection: true,
                                              lockVisible: true,
                                              flex: 1,
                                          },
                                      ],
                                      changeFilterHandler,
                                  },
                              };
                          }
                          if (child?.field === 'workInSum') {
                              return {
                                  ...child,
                                  headerClass: (child.headerClass as string)?.replace('noWrap', ''),
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      withSearch: false,
                                      hideTableHeader: true,
                                      variables: filterVariables,
                                      dataAccessor: 'uerWorkOrderPriceItemWorkInSumFilter',
                                      query: uerWorkOrderPriceItemWorkInSumFilterGql,
                                      childFieldAccessor: 'value',
                                      whereVariables: ['value'],
                                      columns: [
                                          {
                                              headerName: 'Участие в суммировании ФО по виду работ',
                                              field: 'value',
                                              checkboxSelection: true,
                                              lockVisible: true,
                                              flex: 1,
                                              valueFormatter: ({ value }: { value: WorkInSumEnum }) =>
                                                  WorkInSumTitles[value],
                                          },
                                      ],
                                      changeFilterHandler,
                                  },
                              };
                          }

                          return child;
                      }),
                  }))
                : []),
            ...seasonsAndLinkedActsColumns,
        ],
        [
            changeFilterHandler,
            checkRowHandler,
            checkedRows,
            costRounding,
            filterVariables,
            hasWorkKind,
            isDisabledByProjectStatus,
            openModalHandler,
            seasonsAndLinkedActsColumns,
            typeWorkColumns,
            workOrderStatus,
        ],
    );

    const isColumnVisible = useCallback(
        (id: string) => {
            if (id === groupNameHeaderName) {
                return selection[id] ?? false;
            }

            return selection[id] ?? true;
        },
        [selection],
    );
    const fullSetSettingsColumns = useMemo(
        () =>
            columns.reduce((acc: ColDef[], column, idx) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean };

                    if (idx < 3 || (idx > 4 && idx < 9)) {
                        newColumn.disabled = true;
                    }

                    acc.push(newColumn);

                    return acc;
                }

                return acc;
            }, []),
        [columns],
    );

    const tableSettingsConfirm = useCallback(
        (visible) => {
            onTableSettingsConfirm({
                settingColumns: fullSetSettingsColumns,
                incomingColumns: visible,
                setSelection,
                setPinned,
            });
        },
        [fullSetSettingsColumns, setPinned, setSelection],
    );
    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [columns, isColumnVisible, pinnedItems]);

    return {
        columns: fullSetColumns,
        fullSetSettingsColumns,
        isColumnVisible,
        tableSettingsConfirm,
        checkedRows,
        resetCheckedRows,
    };
};
